import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const products = [
  {
    id: 1,
    name: "LED Spot Strip",
    description: "High-brightness warm white LED strip with individual spots for accent lighting",
    image: "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/xpm69zd1_20241228145340797B7AFD01770803_b.jpg",
    features: ["Warm White", "High Brightness", "Flexible Design"]
  },
  {
    id: 2,
    name: "RGB Wall Washer",
    description: "Vibrant RGB linear light perfect for wall washing and ambient effects",
    image: "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/cmc2v86i_20241231175109F9B76F16E0DD2300_b.jpg",
    features: ["Full RGB", "Wall Washing", "Dynamic Colors"]
  },
  {
    id: 3,
    name: "COB LED Strip",
    description: "Continuous output LED strip for seamless, dot-free illumination",
    image: "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/khsikcov_20241231180510ECEDD88EE77FE621_b.jpg",
    features: ["Dot-Free", "24V DC", "3M Adhesive"]
  },
  {
    id: 4,
    name: "Neon Flex",
    description: "Flexible neon tube lighting available in multiple colors for creative designs",
    image: "https://customer-assets.emergentagent.com/job_brand-story-hub-9/artifacts/y207i3li_2024123115325166FF6936E81B4422_b.jpg",
    features: ["Flexible", "Multi-Color", "Waterproof"]
  }
];

const ProductsSection = () => {
  return (
    <section className="py-20 sm:py-24 md:py-32 bg-background" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="text-xs font-semibold tracking-widest uppercase text-primary mb-4 block">
            Our Products
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight">
            Premium LED Lighting
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-light">
            Discover our range of high-quality Illumidel LED products designed for lasting performance
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {products.map((product) => (
            <div
              key={product.id}
              className="group relative bg-card rounded-2xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-500"
              data-testid={`product-card-${product.id}`}
            >
              {/* Product Image */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              {/* Product Info */}
              <div className="p-5">
                <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {product.name}
                </h3>
                <p className="text-sm text-muted-foreground font-light mb-4 line-clamp-2">
                  {product.description}
                </p>
                
                {/* Features Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {product.features.map((feature) => (
                    <span
                      key={feature}
                      className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <Link
                  to="/quote"
                  className="inline-flex items-center text-sm font-medium text-primary hover:gap-3 gap-2 transition-all duration-300"
                >
                  Get Quote
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Button asChild size="lg" variant="outline" className="rounded-full px-8">
            <Link to="/quote">
              Request Product Catalog
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>

        {/* Photo Credits */}
        <p className="text-xs text-muted-foreground/60 text-center mt-8">Photo credits: Illumidel</p>
      </div>
    </section>
  );
};

export default ProductsSection;
